import { FC, useCallback, useMemo, useState } from 'react';

import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button from '../../../../components/button/index';
import { Radio, RadioGroup } from '../../../../components/radio-group/index';
import { Switch } from '../../../../components/switch/index';
import {
  CapTableTab,
  capTableTabTitleTitleMap,
  columnTitle,
  ColumnVariant,
  customizeColumns,
  CustomView,
  StakeholdersViewBy,
} from './fundraising.types';

type CustomizeModalProps = {
  onCancel: () => void;
  onSuccess: (settings: CustomView) => void;
  selectedCapTableTab: CapTableTab;
  viewSettings: CustomView;
};

export type CustomViewSettings = Record<
  CapTableTab,
  {
    columns: Record<ColumnVariant, boolean>;
    viewBy?: { stakeholders: boolean; type: boolean };
  }
>;

const defaultShareClassColumns = customizeColumns[CapTableTab.BY_SHARE_CLASS].reduce(
  (acc, curr) => ({ ...acc, [curr]: false }),
  {} as Record<ColumnVariant, boolean>,
);

const defaultStakeholderColumns = customizeColumns[CapTableTab.BY_STAKEHOLDER].reduce(
  (acc, curr) => ({ ...acc, [curr]: false }),
  {} as Record<ColumnVariant, boolean>,
);

export const CustomizeModal: FC<CustomizeModalProps> = ({
  onCancel,
  onSuccess,
  viewSettings,
  selectedCapTableTab,
}) => {
  const [selectedCustomViewTab, setSelectedCustomViewTab] =
    useState<CapTableTab>(selectedCapTableTab);

  const selectedShareClassColumns = useMemo(
    () =>
      viewSettings[CapTableTab.BY_SHARE_CLASS].columns.reduce(
        (acc, curr) => ({ ...acc, [curr]: true }),
        {},
      ),
    [viewSettings],
  );

  const selectedStakeholdersColumns = useMemo(
    () =>
      viewSettings[CapTableTab.BY_STAKEHOLDER].columns.reduce(
        (acc, curr) => ({ ...acc, [curr]: true }),
        {},
      ),
    [viewSettings],
  );

  const [customSettings, setCustomSettings] = useState<CustomViewSettings>({
    [CapTableTab.BY_SHARE_CLASS]: {
      columns: { ...defaultShareClassColumns, ...selectedShareClassColumns },
    },
    [CapTableTab.BY_STAKEHOLDER]: {
      viewBy: viewSettings[CapTableTab.BY_STAKEHOLDER].viewBy,
      columns: { ...defaultStakeholderColumns, ...selectedStakeholdersColumns },
    },
  });

  const customSettingSelectedTab = customSettings[selectedCustomViewTab];

  const handleChangeViewBy = (field: StakeholdersViewBy, state: boolean) =>
    setCustomSettings((prev) => {
      const newState = {
        ...prev,
        [selectedCustomViewTab]: {
          ...prev[selectedCustomViewTab],
          viewBy: { ...prev[selectedCustomViewTab].viewBy, [field]: state },
        },
      };
      return newState;
    });

  const handleChangeColumnView = (field: ColumnVariant, state: boolean) =>
    setCustomSettings((prev) => {
      const newState = {
        ...prev,
        [selectedCustomViewTab]: {
          ...prev[selectedCustomViewTab],
          columns: { ...prev[selectedCustomViewTab].columns, [field]: state },
        },
      };
      return newState;
    });

  const handleSuccess = useCallback(() => {
    onSuccess({
      [CapTableTab.BY_SHARE_CLASS]: {
        viewBy: customSettings[CapTableTab.BY_SHARE_CLASS].viewBy,
        columns: Object.entries(customSettings[CapTableTab.BY_SHARE_CLASS].columns)
          .filter(([_, state]) => state)
          .map(([field]) => field),
      },
      [CapTableTab.BY_STAKEHOLDER]: {
        viewBy: customSettings[CapTableTab.BY_STAKEHOLDER].viewBy,
        columns: Object.entries(customSettings[CapTableTab.BY_STAKEHOLDER].columns)
          .filter(([_, state]) => state)
          .map(([field]) => field),
      },
    });
  }, [customSettings, onSuccess]);

  const isAtLeastSelectedOneViewBy =
    customSettingSelectedTab?.viewBy?.stakeholders ||
    customSettingSelectedTab?.viewBy?.type ||
    selectedCustomViewTab === CapTableTab.BY_SHARE_CLASS;

  return (
    <div className="flex w-[530px] flex-col gap-4 rounded bg-white p-6">
      <span className="border-b border-b-gray-100 pb-4 text-xl font-semibold text-gray-700">
        <AppFormattedMessage id="CUSTOMIZE_VIEW" />
      </span>
      <div className="flex flex-col gap-2 border-b border-b-gray-100 pb-4">
        <span className="text-xs font-[450] text-gray-500">
          <AppFormattedMessage id="BY" />
        </span>

        <div className="w-fit rounded-3xl border border-gray-200 p-1">
          <RadioGroup
            onChange={setSelectedCustomViewTab}
            value={selectedCustomViewTab}
            wrapperClassName="w-full grow flex-1"
          >
            {capTableTabTitleTitleMap.map(([key, title]) => (
              <Radio
                className="h-8 !w-fit rounded-[32px] border-transparent bg-transparent !px-3 !py-2 text-xs font-[450] text-gray-700 data-[checked]:font-[550] data-[checked]:text-brand-25"
                key={key}
                value={key}
              >
                <span className="text-nowrap">
                  <AppFormattedMessage id={title} />
                </span>
              </Radio>
            ))}
          </RadioGroup>
        </div>
      </div>

      {selectedCustomViewTab === CapTableTab.BY_STAKEHOLDER && (
        <div className="flex flex-col gap-4 border-b border-b-gray-100 pb-4">
          <span className="text-xs font-[450] text-gray-500">
            <AppFormattedMessage id="VIEW_BY" />
          </span>

          <div className="mt-2 flex gap-2">
            <Switch
              checked={customSettingSelectedTab?.viewBy?.type}
              key={`${selectedCustomViewTab}view-by-__type_switch`}
              onChange={(state) => handleChangeViewBy(StakeholdersViewBy.TYPE, state)}
            />
            <span className="text-xs font-[450] text-gray-700">
              <AppFormattedMessage id="TYPE" />
            </span>
          </div>
          <div className="flex gap-2">
            <Switch
              checked={customSettingSelectedTab?.viewBy?.stakeholders}
              key={`${selectedCustomViewTab}view-by-stakeholders_switch`}
              onChange={(state) => handleChangeViewBy(StakeholdersViewBy.STAKEHOLDERS, state)}
            />
            <span className="text-xs font-[450] text-gray-700">
              <AppFormattedMessage id="STAKEHOLDERS" />
            </span>
          </div>

          {!isAtLeastSelectedOneViewBy && (
            <span className="text-xs font-[450] text-fireside-600">
              <AppFormattedMessage id="SELECT_AT_LEAST_ONE_VIEW_BY" />
            </span>
          )}
        </div>
      )}

      <div className="flex flex-col gap-4 border-b border-b-gray-100 pb-4">
        <span className="text-xs font-[450] text-gray-500">
          <AppFormattedMessage id="COLUMNS" />
        </span>

        <div className="grid max-h-[300px] grid-flow-row grid-cols-2 gap-4 overflow-y-auto">
          {customizeColumns[selectedCustomViewTab].map(
            (columnKey) =>
              columnTitle[columnKey] && (
                <div className="mt-2 flex gap-2" key={`${columnKey}_${selectedCustomViewTab}`}>
                  <Switch
                    checked={customSettingSelectedTab.columns[columnKey]}
                    onChange={(state) => handleChangeColumnView(columnKey, state)}
                  />
                  <span className="text-xs font-[450] text-gray-700">
                    <AppFormattedMessage id={columnTitle[columnKey]} />
                  </span>
                </div>
              ),
          )}
        </div>
      </div>
      <div className="flex items-center justify-between">
        <div className="flex h-11 gap-4">
          <Button
            className="h-full w-fit rounded border border-gray-300 px-6 text-sm font-[450] text-gray-700"
            onClick={onCancel}
            styleType="NONE"
          >
            <AppFormattedMessage id="CANCEL" />
          </Button>
          <Button
            className="h-full w-fit px-6 text-sm font-[550] text-white"
            disabled={!isAtLeastSelectedOneViewBy}
            onClick={handleSuccess}
          >
            <AppFormattedMessage id="SAVE" />
          </Button>
        </div>
      </div>
    </div>
  );
};
